import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { brl } from "@/lib/api";
import { DollarSign, ShoppingBag, Users, AlertTriangle, ScanBarcode, ArrowRight, LayoutDashboard } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import useFetch from "@/hooks/useFetch";
import { KpiCard, KpiSkeleton, ErrorState, StatusBadge, TableSkeleton } from "@/components/panel/ui";
import { Card, CardBody, CardHeader, PageHeader, Scale } from "@/components/ui-kit";
import { usePanelLink } from "@/components/panel/screens";

export default function Dashboard() {
  // Link para o painel ATUAL: a mesma tela serve admin, vendedor e balcão.
  const link = usePanelLink();
  const { data, loading, error, retry } = useFetch("/admin/dashboard");
  const { data: sales } = useFetch("/admin/reports/sales", { days: 14 });
  const navigate = useNavigate();

  // últimos 7 dias vs 7 anteriores (para o delta e o gráfico)
  const { series, delta } = useMemo(() => {
    if (!sales?.series) return { series: [], delta: null };
    const cutoff = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
    const last7 = sales.series.filter((d) => d.date >= cutoff);
    const prev7 = sales.series.filter((d) => d.date < cutoff);
    const sum = (l) => l.reduce((s, d) => s + d.online + d.balcao, 0);
    const cur = sum(last7);
    const prev = sum(prev7);
    return { series: last7, delta: prev > 0 ? ((cur - prev) / prev) * 100 : null };
  }, [sales]);

  if (error) return <ErrorState onRetry={retry} />;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <PageHeader
        eyebrow="Visão Geral"
        title="Dashboard"
        icon={LayoutDashboard}
        description="Receita, pedidos e estoque do dia. Loja online e balcão aparecem separados — o estoque é o mesmo."
      />

      {loading ? <KpiSkeleton /> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={DollarSign} label="Receita Online" value={brl(data.revenue_online)} testId="kpi-revenue-online" />
          <KpiCard icon={ScanBarcode} label="Receita Balcão (PDV)" value={brl(data.revenue_pos)} testId="kpi-revenue-pos" />
          <KpiCard icon={ShoppingBag} label="Pedidos" value={data.orders_count + data.pos_count} testId="kpi-orders" />
          <KpiCard icon={Users} label="Clientes" value={data.customers_count} testId="kpi-customers" />
        </div>
      )}

      {series.length > 0 && (
        <Card data-testid="dashboard-chart">
          {/* Título por children, não por `title`: o slot do kit é uma linha
              flex, e o delta precisa FLUIR com o texto para quebrar junto no
              celular em vez de esticar o cabeçalho. */}
          <CardHeader action={<Link to={link("relatorios")} data-testid="dashboard-reports-link" className="text-brand-600 text-xs font-semibold hover:underline inline-flex items-center h-9 md:h-auto px-2 -mx-2 md:px-0 md:mx-0 rounded-lg hover:bg-brand-50 md:hover:bg-transparent">Relatórios →</Link>}>
            <p className="font-semibold text-slate-900 text-sm">Receita — últimos 7 dias
              {delta != null && (
                <span className={`ml-2 text-xs font-semibold ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {delta >= 0 ? "↑" : "↓"} {Math.abs(delta).toFixed(0)}% vs semana anterior
                </span>
              )}
            </p>
          </CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={series} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(8, 10) + "/" + d.slice(5, 7)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => brl(v)} labelFormatter={(d) => new Date(d + "T12:00:00").toLocaleDateString("pt-BR")} />
                <Area type="monotone" dataKey="online" stroke="var(--brand)" fill="var(--brand)" fillOpacity={0.2} name="Online" />
                <Area type="monotone" dataKey="balcao" stroke="var(--navy)" fill="var(--navy)" fillOpacity={0.2} name="Balcão" />
              </AreaChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}

      {/* As duas listas abaixo são RESUMOS dentro de um card, não listagens de
          registros: não têm filtro, paginação nem ações de linha, e a saída é o
          link do cabeçalho ("Ver todos", "Repor"). Por isso seguem em <table>
          simples — DataTable desenha o próprio Card e aninharia superfície. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader
            title="Pedidos recentes"
            action={<Link to={link("pedidos")} data-testid="dashboard-orders-link" className="text-brand-600 text-xs font-semibold hover:underline inline-flex items-center h-9 md:h-auto px-2 -mx-2 md:px-0 md:mx-0 rounded-lg hover:bg-brand-50 md:hover:bg-transparent">Ver todos →</Link>}
          />
          {loading ? <TableSkeleton rows={4} cols={4} /> : (
            <table className="w-full text-sm">
              <caption className="sr-only">Últimos pedidos recebidos</caption>
              <tbody>
                {data.recent_orders.map((o) => (
                  <tr key={o.id} onClick={() => navigate(link("pedidos"))}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" data-testid="recent-order-row">
                    <td className="px-5 py-3 font-mono tabular-nums text-slate-900">{o.number}</td>
                    <td className="px-3 py-3 text-slate-600">{o.customer_name}</td>
                    <td className="px-3 py-3"><StatusBadge value={o.status} /></td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums">{brl(o.total)}</td>
                  </tr>
                ))}
                {data.recent_orders.length === 0 && <tr><td colSpan={4} className="px-5 py-6 text-slate-400">Nenhum pedido ainda.</td></tr>}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          {/* Idem: o `icon` do CardHeader sai em slate-400 (ícone decorativo de
              seção) e aqui o âmbar é o próprio aviso — é o que diferencia este
              card do de pedidos numa varrida. */}
          <CardHeader action={<Link to={link("estoque")} data-testid="dashboard-stock-link" className="text-brand-600 text-xs font-semibold hover:underline inline-flex items-center h-9 md:h-auto px-2 -mx-2 md:px-0 md:mx-0 rounded-lg hover:bg-brand-50 md:hover:bg-transparent">Repor →</Link>}>
            <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" aria-hidden /> Estoque baixo
            </p>
          </CardHeader>
          {loading ? <TableSkeleton rows={4} cols={3} /> : (
            <table className="w-full text-sm">
              <caption className="sr-only">Variações com estoque baixo</caption>
              <tbody>
                {data.low_stock.map((s, i) => (
                  <tr key={i} onClick={() => navigate(link("estoque"))}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" data-testid="low-stock-row">
                    <td className="px-5 py-3 text-slate-800">{s.name}</td>
                    <td className="px-3 py-3 text-slate-500">{s.variant}</td>
                    <td className="px-5 py-3 text-right">
                      <Scale value={s.stock} label={`${s.name} ${s.variant}: ${s.stock} em estoque`} className="justify-end" />
                    </td>
                  </tr>
                ))}
                {data.low_stock.length === 0 && <tr><td colSpan={3} className="px-5 py-6 text-slate-400 flex items-center gap-2"><ArrowRight size={14} className="text-emerald-500" aria-hidden />Nenhum item com estoque baixo.</td></tr>}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
